import React, { useEffect, useMemo, useState } from "react";
import {
  useGame,
  usePlayer,
  usePlayers,
} from "@empirica/core/player/classic/react";
import { Button } from "../components/Button";
import { getCounterpartCopy, getNegotiationTermCopy } from "../payoffFrame";
import { removeContinueMessageMarkers } from "../../../shared/chatbotInitialMessage.js";

function normalizeChatbotArm(value) {
  if (value === "treatment") {
    return "treatment";
  }

  return "control";
}

const TREATMENT_INITIAL_MESSAGE_CONTEXT =
  "I played two rounds of a game where I had to keep my mouse inside a moving circle.";
const TREATMENT_INITIAL_MESSAGE_STARTER = `${TREATMENT_INITIAL_MESSAGE_CONTEXT} (please continue the message).`;

function getParticipantWrittenMessage(message, isTreatment) {
  if (!isTreatment || !message.startsWith(TREATMENT_INITIAL_MESSAGE_CONTEXT)) {
    return message;
  }

  return message.slice(TREATMENT_INITIAL_MESSAGE_CONTEXT.length);
}

export function ChatbotContextStage() {
  const game = useGame();
  const player = usePlayer();
  const players = usePlayers();
  const [showChatbotNotice, setShowChatbotNotice] = useState(true);
  const termCopy = getNegotiationTermCopy(game.get("design"));
  const counterpartLabel = getCounterpartCopy(game.get("design")).term;
  const chatbotArm = normalizeChatbotArm(player.get("chatbotArm"));
  const isTreatment = chatbotArm === "treatment";
  const [message, setMessage] = useState(() => {
    const savedMessage = player.get("chatbotInitialMessage");
    if (typeof savedMessage === "string") {
      return savedMessage;
    }

    return isTreatment ? `${TREATMENT_INITIAL_MESSAGE_STARTER} ` : "";
  });
  const [showError, setShowError] = useState(false);

  const allSubmitted = useMemo(
    () => players.every((p) => p.stage.get("submit")),
    [players]
  );

  useEffect(() => {
    player.stage.set("chatbotInitialMessageDraft", message);
  }, [message, player]);

  function handleSubmit(event) {
    event.preventDefault();
    const trimmedMessage = removeContinueMessageMarkers(message).trim();
    const participantWrittenMessage = getParticipantWrittenMessage(
      trimmedMessage,
      isTreatment
    ).trim();

    if (!participantWrittenMessage) {
      setShowError(true);
      return;
    }

    setShowError(false);
    setMessage(trimmedMessage);
    player.set("chatbotInitialMessage", trimmedMessage);
    player.set(
      "chatbotInitialMessageMode",
      isTreatment ? "negotiation_chatbot_context" : "filler_chatbot_context"
    );
    player.stage.set("chatbotInitialMessage", trimmedMessage);
    player.stage.set("submit", true);
  }

  function handleCancelSubmit() {
    player.stage.set("submit", false);
  }

  const alreadySubmitted = Boolean(player.stage.get("submit"));

  return (
    <>
      {showChatbotNotice ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="chatbot-notice-description"
            className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl"
          >
            <p
              id="chatbot-notice-description"
              className="text-xl leading-relaxed text-gray-900"
            >
              You will now have a brief conversation with a smart chatbot before
              returning to the negotiation.
            </p>
            <div className="mt-6 flex justify-end">
              <Button
                handleClick={() => setShowChatbotNotice(false)}
                autoFocus
              >
                ok
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <div
        className="py-10 max-w-2xl mx-auto px-8 space-y-6"
        aria-hidden={showChatbotNotice}
        inert={showChatbotNotice ? "" : undefined}
      >
        <form
          className="rounded-md border border-gray-200 p-5 space-y-4"
          onSubmit={handleSubmit}
        >
        <div className="space-y-2">
          <h2 className="text-3xl font-semibold text-gray-900">
            Chatbot Conversation
          </h2>
          <p className="text-xl text-gray-700 whitespace-pre-line">
            {isTreatment ? (
              <>
                Before you submit {termCopy.article} {termCopy.term} to your{" "}
                {counterpartLabel},{" "}
                <strong>
                  you have a couple minutes to chat with a smart chatbot about your situation
                </strong>
                .
                <br />
                <br />
                The chatbot can help you{" "}
                <strong>
                  understand the situation, think things through, or strategize
                </strong>
                .
                <br />
                <br />
                <strong>
                  THE CHATBOT HAS NO INFORMATION ABOUT THE TASK EXCEPT WHAT YOU WRITE BELOW.
                </strong>
                <br />
                <br />
                Please give it the information you think will{" "}
                <strong>
                   help it help you
                  </strong>,
                <br />
                as you would using a typical chatbot like ChatGPT, Claude, or Gemini.
                <br />
                <br />
                Note: Your {counterpartLabel} will not see this conversation.
              </>
            ) : (
              <>
                Discuss with a smart chatbot whether cats or dogs make better pets.
                <br />
                <br />
                Before the chat, explain your position on whether cats or dogs make better
                pets.
                <br />
                <br />
                The chatbot will read your explanation before the conversation.
              </>
            )}
          </p>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="chatbot-initial-message"
            className="block text-2xl font-medium text-gray-900"
          >
            Initial message
          </label>
          <textarea
            id="chatbot-initial-message"
            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-empirica-500 focus:border-empirica-500 text-lg"
            rows={8}
            placeholder={
              isTreatment
                ? "Tell the chatbot what has happened, what your situation is, and what you want help thinking through..."
                : "Explain whether you prefer cats or dogs as pets, and why..."
            }
            value={message}
            onChange={(event) => {
              setMessage(event.target.value);
              if (showError && event.target.value.trim()) {
                setShowError(false);
              }
            }}
            disabled={alreadySubmitted}
          />
          {showError ? (
            <p className="text-sm text-red-600">
              Please write an initial message before continuing.
            </p>
          ) : null}
        </div>

        <div className="flex items-center space-x-4">
          <Button
            type="submit"
            autoFocus={!showChatbotNotice}
            disabled={alreadySubmitted}
          >
            Continue
          </Button>
          {alreadySubmitted && !allSubmitted ? (
            <Button handleClick={handleCancelSubmit}>Cancel Continue</Button>
          ) : null}
          {alreadySubmitted && !allSubmitted ? (
            <span className="text-sm text-gray-500">
              Waiting for the other participant...
            </span>
          ) : null}
        </div>
        </form>
      </div>
    </>
  );
}
